import { on } from '@ember/modifier';
import type Owner from '@ember/owner';
import { service } from '@ember/service';

import type { PopupData, Service } from 'webapp-types';
import { TrackingEvent } from 'webapp/enums/tracking';

import CommonRequestTrialLinkComponent from 'webapp/components/common/request-trial-link';
import CommonUiIconComponent from 'webapp/components/common/ui-icon';
import CommonUpgradePlanLinkComponent from 'webapp/components/common/upgrade-plan-link';
import type { Args } from 'webapp/components/popup/inner/context';
import PopupInnerContextComponent from 'webapp/components/popup/inner/context';

import type Promotion from 'webapp/models-custom/promotion';

export default class MetricDetailsExploreModulesDataVisualizationToolbarAnomalyDetectionPopupForbidenFeatureComponent extends PopupInnerContextComponent<PopupData.Anomaly.Forbidden> {
	@service('account') declare accountService: Service.Account;
	@service('billing') declare billingService: Service.Billing;
	@service('analytics') declare analyticsService: Service.Analytics;
	@service('flow/promotions') declare flowPromotionsService: Service.Flow.Promotions;

	constructor(owner: Owner, args: Args) {
		super(owner, args);

		if (!this.popupData.sendTrackingEvent) {
			this.sendTrackingEvent();
		}
	}

	get availableTrialPromotion(): Promotion | undefined {
		const planForFeature = this.billingService.getPlanForTrial(this.popupData.billingFeature);
		return planForFeature
			? this.flowPromotionsService.getAvailableTrialPromotion(planForFeature)
			: undefined;
	}

	get canRequestTrial() {
		return this.billingService.canRequestTrial || !!this.availableTrialPromotion;
	}

	private sendTrackingEvent() {
		this.analyticsService.event(TrackingEvent.LIMITATION_POPUP, {
			feature: this.popupData.billingFeature,
		});
	}
	<template>
		<div class="header">
			<a role="button" class="close-icon" {{on "click" this.close}}>
				<CommonUiIconComponent @name="Close" @size="sm" />
			</a>
		</div>

		<div class="body">
			<img
				src="/images/metric-insights/anomaly.png"
				alt="Anomaly detection"
				width="212"
				height="96"
				loading="lazy"
			/>

			<h4>
				{{this.popupData.title}}
			</h4>
			<p>
				{{this.popupData.description}}
			</p>

			{{#unless this.accountService.hideDataboxNotWhiteLabeledContentForNonAdmins}}
				<a
					class="learn-more"
					href="https://help.databox.com/detect-anomalies-in-your-data"
					target="_blank"
				>
					<CommonUiIconComponent @name="Book" @size="sm" />
					Learn more
				</a>
			{{/unless}}

			{{#if this.popupData.showUpgradeLink}}
				<div class="warning">
					<span class="warning-icon">
						<CommonUiIconComponent @name="Lock" @size="sm" />
					</span>

					<p>
						Upgrade to Databox Growth to use this analytics feature.

						<CommonUpgradePlanLinkComponent
							@text="Explore the plan"
							@feature={{this.popupData.billingFeature}}
						/>
					</p>
				</div>
			{{/if}}

			{{#if this.popupData.showUpgradeActions}}
				<div class="warning">
					<span class="warning-icon">
						<CommonUiIconComponent @name="Lock" @size="sm" />
					</span>

					<p>
						This feature is not available with your current plan.

						{{#if this.canRequestTrial}}
							<CommonRequestTrialLinkComponent
								@feature={{this.popupData.billingFeature}}
							/>
						{{else}}
							<CommonUpgradePlanLinkComponent
								@feature={{this.popupData.billingFeature}}
							/>
						{{/if}}
					</p>
				</div>
			{{/if}}
		</div>
	</template>
}
